import { useState, useEffect, useCallback } from 'react'
import { usePubSub } from '@services'
import { storageHelpers } from '@utils'
import { PubSubService } from '@services'
import { usePubsubRedux } from './useRedux'
import { useConnectionRetry } from './useConnectionRetry'

export const useCashRegister = () => {
    const [isAutoJoining, setIsAutoJoining] = useState(false)
    const { setActiveGroupId } = usePubsubRedux()
    const { groups, joinGroup, waitUntilConnected } = usePubSub()
    const { executeWithRetry } = useConnectionRetry(5)

    const joinedGroupName = Array.isArray(groups) && groups.length > 0 ? groups[0] : null

    // Auto-join group when page loads - wait for connection first
    useEffect(() => {
        const autoJoinGroup = async () => {
            if (isAutoJoining || joinedGroupName) return

            setIsAutoJoining(true)

            try {
                await executeWithRetry(
                    async () => {
                        await waitUntilConnected(5000)

                        // Double-check: verify WebSocket is actually connected
                        const socketReady = PubSubService.socket && PubSubService.socket.readyState === WebSocket.OPEN
                        if (!socketReady) {
                            throw new Error('WebSocket readyState is not OPEN')
                        }

                        return true
                    },
                    {
                        onRetry: (attempt, delay) => {
                            console.log(`Connection retry ${attempt}, waiting ${delay}ms`)
                        },
                        onFailure: error => {
                            console.error('Failed to establish connection:', error)
                        },
                    }
                )

                // Get the last used cash register ID from localStorage
                const cashRegisterId = storageHelpers.getCheckoutAssistantCashRegisterId()
                if (cashRegisterId) {
                    await joinGroup(cashRegisterId)
                }
            } catch (err) {
                console.error('Failed to auto-join group:', err)
            } finally {
                setIsAutoJoining(false)
            }
        }

        autoJoinGroup()
    }, [joinGroup, waitUntilConnected, joinedGroupName, executeWithRetry])

    // Save the current group to localStorage when it changes
    useEffect(() => {
        if (joinedGroupName) {
            storageHelpers.setCheckoutAssistantCashRegisterId(joinedGroupName)
            setActiveGroupId(joinedGroupName.split('-')[1])
        }
    }, [joinedGroupName, setActiveGroupId])

    return {
        joinedGroupName,
        isAutoJoining,
    }
}
